import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useCart } from "@/hooks/useCart";
import { useProducts } from "@/hooks/useProducts";
import ImageGallery from "./components/ImageGallery";
import ProductInfo from "./components/ProductInfo";
import RelatedProducts from "./components/RelatedProducts";
import ReviewsSection from "./components/ReviewsSection";

export default function ProductPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getById, products, loading } = useProducts();
  const product = getById(Number(id));
  const { totalItems, openCart } = useCart();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  useEffect(() => {
    if (!product) return;

    const siteUrl = import.meta.env.VITE_SITE_URL ?? "https://example.com";
    const productUrl = `${siteUrl}/product/${product.id}`;

    document.title = `${product.nameAr} — متجر الإبداع AL Ebdaa`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", `${product.nameAr} — ${product.shortDesc ?? ""} بسعر ${product.price.toLocaleString()} ر.س. اشترِ الآن من متجر الإبداع AL Ebdaa.`);
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.nameAr,
      "description": product.shortDesc ?? product.nameAr,
      "image": product.images?.[0] ?? "",
      "url": productUrl,
      "sku": String(product.id),
      "brand": {
        "@type": "Brand",
        "name": "متجر الإبداع AL Ebdaa"
      },
      "offers": {
        "@type": "Offer",
        "url": productUrl,
        "priceCurrency": "SAR",
        "price": product.price,
        "availability": "https://schema.org/InStock",
        "seller": {
          "@type": "Organization",
          "name": "متجر الإبداع AL Ebdaa"
        }
      },
      "aggregateRating": product.reviews > 0 ? {
        "@type": "AggregateRating",
        "ratingValue": product.rating,
        "reviewCount": product.reviews,
        "bestRating": 5,
        "worstRating": 1
      } : undefined
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "schema-product";
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById("schema-product");
      if (el) el.remove();
    };
  }, [product]);

  // Still loading from Supabase — show skeleton
  if (loading || (!product && products.length === 0)) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center text-center p-8" dir="rtl">
        <div className="w-16 h-16 flex items-center justify-center mb-6">
          <i className="ri-loader-4-line text-4xl text-amber-400 animate-spin"></i>
        </div>
        <p className="text-gray-500 text-sm" style={{ fontFamily: "'Tajawal', sans-serif" }}>
          جاري تحميل المنتج...
        </p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center text-center p-8" dir="rtl">
        <div className="w-20 h-20 flex items-center justify-center bg-amber-400/10 border border-amber-400/20 rounded-2xl mb-6">
          <i className="ri-error-warning-line text-4xl text-amber-400"></i>
        </div>
        <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Amiri', 'Tajawal', serif" }}>
          المنتج غير موجود
        </h2>
        <p className="text-gray-500 mb-8" style={{ fontFamily: "'Tajawal', sans-serif" }}>
          لم نتمكن من العثور على هذا المنتج
        </p>
        <Link
          to="/"
          className="bg-gradient-to-r from-amber-400 to-amber-500 text-black font-bold px-8 py-3 rounded-full hover:from-amber-300 hover:to-amber-400 transition-all whitespace-nowrap cursor-pointer"
          style={{ fontFamily: "'Tajawal', sans-serif" }}
        >
          العودة للرئيسية
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]" dir="rtl">
      {/* Top Nav Bar */}
      <div className="sticky top-0 z-40 bg-[#0a0a0a]/95 backdrop-blur-md border-b border-amber-400/10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          {/* Back */}
          <Link
            to="/"
            className="flex items-center gap-2 text-gray-400 hover:text-amber-400 transition-colors text-sm cursor-pointer group"
            style={{ fontFamily: "'Tajawal', sans-serif" }}
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-lg border border-white/10 group-hover:border-amber-400/30 transition-all">
              <i className="ri-arrow-right-line text-sm"></i>
            </div>
            <span className="hidden sm:block">العودة للمتجر</span>
          </Link>

          {/* Logo Mark */}
          <Link to="/" className="flex items-center gap-2 cursor-pointer group">
            <div className="relative w-9 h-9 flex items-center justify-center flex-shrink-0">
              <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-amber-400/20 to-amber-600/10 border border-amber-400/30 group-hover:border-amber-400/60 transition-all duration-300"></div>
              <span
                className="relative text-amber-400 font-black text-base leading-none"
                style={{ fontFamily: "'Amiri', serif", textShadow: "0 0 10px rgba(251,191,36,0.5)" }}
              >
                إ
              </span>
            </div>
            <div className="hidden sm:flex flex-col leading-none">
              <span
                className="font-black text-base tracking-wide bg-gradient-to-l from-amber-300 via-amber-400 to-amber-500 bg-clip-text text-transparent"
                style={{ fontFamily: "'Amiri', serif" }}
              >
                إبداع
              </span>
              <span className="text-[8px] text-amber-400/40 tracking-[0.2em] uppercase" style={{ fontFamily: "'Tajawal', sans-serif" }}>
                AL EBDAA
              </span>
            </div>
          </Link>

          {/* Right actions */}
          <div className="flex items-center gap-3">
            {/* Breadcrumb */}
            <div className="hidden md:flex items-center gap-2 text-xs text-gray-500" style={{ fontFamily: "'Tajawal', sans-serif" }}>
              <Link to="/" className="hover:text-amber-400 transition-colors cursor-pointer">الرئيسية</Link>
              <i className="ri-arrow-left-s-line"></i>
              <span className="text-amber-400/70">{product.category}</span>
              <i className="ri-arrow-left-s-line"></i>
              <span className="text-gray-400 truncate max-w-32">{product.nameAr}</span>
            </div>
            {/* Cart */}
            <button
              onClick={openCart}
              className="relative w-10 h-10 flex items-center justify-center text-gray-300 hover:text-amber-400 transition-colors cursor-pointer"
            >
              <i className="ri-shopping-cart-line text-xl"></i>
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center bg-amber-400 text-black text-xs font-black rounded-full">
                  {totalItems > 9 ? "9+" : totalItems}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Ambient gold glow top */}
      <div className="absolute top-16 left-0 right-0 h-64 bg-[radial-gradient(ellipse_at_top,_rgba(251,191,36,0.05)_0%,_transparent_70%)] pointer-events-none"></div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12 relative">
        {/* Product Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          <div className="w-full">
            <ImageGallery images={product.images} name={product.nameAr} />
          </div>
          <div className="w-full">
            <ProductInfo product={product} />
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: "ri-shield-check-line", label: "ضمان سنة كاملة", sub: "على جميع المنتجات", color: "text-emerald-400", border: "border-emerald-400/20", bg: "bg-emerald-400/5" },
              { icon: "ri-truck-line", label: "توصيل سريع", sub: "لجميع أنحاء المملكة", color: "text-amber-400", border: "border-amber-400/20", bg: "bg-amber-400/5" },
              { icon: "ri-exchange-line", label: "إرجاع مجاني", sub: "خلال 14 يوم", color: "text-sky-400", border: "border-sky-400/20", bg: "bg-sky-400/5" },
              { icon: "ri-customer-service-2-line", label: "دعم 24/7", sub: "نحن دائماً هنا", color: "text-rose-400", border: "border-rose-400/20", bg: "bg-rose-400/5" },
            ].map((badge, i) => (
              <div
                key={i}
                className={`${badge.bg} border ${badge.border} rounded-2xl p-5 text-center transition-all duration-300 hover:scale-[1.02]`}
              >
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <i className={`${badge.icon} ${badge.color} text-2xl`}></i>
                </div>
                <p className="text-white text-sm font-bold mb-1" style={{ fontFamily: "'Tajawal', sans-serif" }}>{badge.label}</p>
                <p className="text-gray-500 text-xs" style={{ fontFamily: "'Tajawal', sans-serif" }}>{badge.sub}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Reviews Section */}
        <ReviewsSection productId={product.id} productName={product.nameAr} />

        {/* Related Products */}
        <RelatedProducts relatedIds={product.relatedIds} currentId={product.id} allProducts={products} />

        {/* Back Button */}
        <div className="text-center mt-16">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 border border-white/10 hover:border-amber-400/30 text-gray-400 hover:text-amber-400 px-8 py-3 rounded-full text-sm transition-all cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Tajawal', sans-serif" }}
          >
            <i className="ri-arrow-right-line"></i>
            العودة للصفحة السابقة
          </button>
        </div>
      </div>

      {/* Footer mini */}
      <div className="border-t border-amber-400/10 mt-16 py-6 text-center">
        <p className="text-gray-600 text-xs" style={{ fontFamily: "'Tajawal', sans-serif" }}>
          © 2026 متجر إبداع — جميع الحقوق محفوظة
        </p>
      </div>
    </div>
  );
}
